import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Send, Mail, Users, TrendingUp, Clock, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminNewsletter() {
  const [data, setData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/newsletter/subscribers"),
      api.get("/admin/newsletter/campaigns"),
    ])
      .then(([s, c]) => {
        setData(s.data);
        setCampaigns(c.data || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const onSend = async () => {
    if (!window.confirm(`Envoyer la newsletter à ${data?.active_count || 0} abonnés actifs ?`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/newsletter/send");
      toast.success(`Newsletter envoyée : ${r.data.sent} succès, ${r.data.failed} échecs`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <p className="mx-auto max-w-4xl px-6 py-10 text-sm text-muted-foreground">Chargement…</p>;

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-10" data-testid="admin-newsletter-page">
      <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="h-4 w-4" /> Admin dashboard
      </Link>

      <div className="eyebrow text-secondary">Admin</div>
      <h1 className="mt-2 font-display text-3xl lg:text-4xl font-black tracking-tight">
        Newsletter
      </h1>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="newsletter-kpis">
        <KPI icon={Users} label="Abonnés actifs" value={data?.active_count || 0} color="text-emerald-600" />
        <KPI icon={Mail} label="Total inscriptions" value={data?.total_count || 0} color="text-blue-600" />
        <KPI icon={TrendingUp} label="Campagnes envoyées" value={campaigns.length} color="text-purple-600" />
        <KPI icon={Clock} label="Dernière campagne" value={campaigns[0]?.sent_at ? new Date(campaigns[0].sent_at).toLocaleDateString("fr-FR") : "—"} color="text-slate-600" small />
      </div>

      {data?.sources && Object.keys(data.sources).length > 0 && (
        <Card className="mt-6 p-5">
          <h3 className="font-semibold text-sm">Sources d'inscription</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(data.sources).map(([src, count]) => (
              <Badge key={src} variant="outline" className="rounded-full">
                {src}: <span className="ml-1 font-bold">{count}</span>
              </Badge>
            ))}
          </div>
        </Card>
      )}

      <Card className="mt-6 p-6 bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="font-display font-bold text-lg">Envoyer la newsletter mensuelle</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Les 5 tutos les plus votés seront inclus. Envoyé à {data?.active_count || 0} abonnés actifs.
            </p>
          </div>
          <Button
            onClick={onSend}
            disabled={sending || (data?.active_count || 0) === 0}
            className="rounded-full h-11 px-6"
            data-testid="btn-send-newsletter"
          >
            <Send className="h-4 w-4 mr-2" />
            {sending ? "Envoi…" : "Envoyer maintenant"}
          </Button>
        </div>
      </Card>

      <h2 className="mt-12 font-display text-xl font-bold">Historique des campagnes</h2>
      {campaigns.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">Aucune campagne envoyée.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {campaigns.map((c) => (
            <Card key={c.id} className="p-4" data-testid={`campaign-${c.id}`}>
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{c.subject}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {new Date(c.sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} · {c.tutos_included.length} tutos
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-sm font-bold text-emerald-600">✓ {c.sent_count}</span>
                  {c.failed_count > 0 && (
                    <span className="ml-3 text-sm text-red-600">✗ {c.failed_count}</span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <h2 className="mt-12 font-display text-xl font-bold">Abonnés récents</h2>
      <div className="mt-4 space-y-1" data-testid="subscribers-list">
        {(data?.items || []).slice(0, 20).map((s) => (
          <div key={s.id} className="flex items-center justify-between px-3 py-2 rounded hover:bg-slate-50 text-sm">
            <span className="font-mono text-xs">{s.email}</span>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] rounded-full">{s.source}</Badge>
              <span className="text-[10px] text-muted-foreground">
                {new Date(s.subscribed_at).toLocaleDateString("fr-FR")}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, small = false }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className={`h-4 w-4 ${color}`} />
        {label}
      </div>
      <div className={`mt-1 font-display font-black tracking-tight ${small ? "text-lg" : "text-2xl"}`}>
        {value}
      </div>
    </Card>
  );
}
